/**
 * Fluid sample lifecycle (docs/field-service/fluid-samples/PRD.md §9.1).
 * Pure: no Firestore, no Date.now(). Times are epoch milliseconds.
 */
export type FluidType = 'oil' | 'coolant' | 'fuel';
export const FLUID_TYPES: readonly FluidType[] = ['oil', 'coolant', 'fuel'];

export type SampleStatus = 'expected' | 'logged' | 'sent' | 'resulted' | 'closed' | 'lost' | 'cancelled';
export const SAMPLE_STATUSES: readonly SampleStatus[] = ['expected', 'logged', 'sent', 'resulted', 'closed', 'lost', 'cancelled'];

export type FsrMatch = 'confirmed' | 'awaiting_fsr' | 'not_on_fsr' | 'unit_mismatch' | 'fsr_only';
export type SampleOrigin = 'fsr' | 'office_intake' | 'lab_result';

const ALLOWED: Readonly<Record<SampleStatus, readonly SampleStatus[]>> = {
  expected: ['logged', 'lost', 'cancelled'],
  logged: ['sent', 'expected', 'lost', 'cancelled'],
  sent: ['logged', 'resulted', 'lost', 'cancelled'],
  resulted: ['closed'],
  closed: [],
  lost: ['resulted'],
  cancelled: [],
};

export function canTransition(from: SampleStatus, to: SampleStatus): boolean {
  return from !== to && ALLOWED[from].includes(to);
}

export function isTerminal(status: SampleStatus): boolean {
  return status === 'closed' || status === 'cancelled';
}

export type StageKey = 'collected' | 'logged' | 'shipped' | 'at_lab' | 'report_received' | 'closed';

export interface StageStep {
  readonly key: StageKey;
  readonly label: string;
  readonly state: 'done' | 'current' | 'todo';
  readonly at: number | null;
  readonly by: string | null;
}

export interface TrackerInput {
  readonly status: SampleStatus;
  readonly collectedAt: number | null;
  readonly loggedAt: number | null;
  readonly loggedBy: string | null;
  readonly sentAt: number | null;
  readonly receivedAtLab: number | null;
  readonly resultedAt: number | null;
  readonly closedAt: number | null;
  readonly closedBy: string | null;
}

const LABELS: Readonly<Record<StageKey, string>> = {
  collected: 'Collected',
  logged: 'Logged',
  shipped: 'Shipped',
  at_lab: 'At lab',
  report_received: 'Report received',
  closed: 'Closed',
};

const ORDER: readonly StageKey[] = ['collected', 'logged', 'shipped', 'at_lab', 'report_received', 'closed'];

/** The stage a sample is currently AT — the latest one it has reached — by status. */
const CURRENT_BY_STATUS: Readonly<Record<SampleStatus, StageKey | null>> = {
  expected: 'collected',
  logged: 'logged',
  sent: 'shipped',
  resulted: 'report_received',
  closed: 'closed',
  lost: null,
  cancelled: null,
};

export function stageTracker(input: TrackerInput): { steps: StageStep[]; offTrack: 'lost' | 'cancelled' | null } {
  const reachedAt: Readonly<Record<StageKey, number | null>> = {
    collected: input.collectedAt,
    logged: input.loggedAt,
    shipped: input.sentAt,
    at_lab: input.receivedAtLab,
    report_received: input.resultedAt,
    closed: input.closedAt,
  };
  const by: Readonly<Record<StageKey, string | null>> = {
    collected: null, logged: input.loggedBy, shipped: null, at_lab: null, report_received: null, closed: input.closedBy,
  };
  const offTrack = input.status === 'lost' || input.status === 'cancelled' ? input.status : null;
  const current = CURRENT_BY_STATUS[input.status];
  const currentIndex = current === null ? -1 : ORDER.indexOf(current);

  const steps = ORDER.map((key, i): StageStep => {
    // Off track: every stage that actually has a date is done; nothing is current (the chip is).
    const state: StageStep['state'] = offTrack
      ? (reachedAt[key] !== null ? 'done' : 'todo')
      : i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'todo';
    const shown = state !== 'todo';
    return { key, label: LABELS[key], state, at: shown ? reachedAt[key] : null, by: shown ? by[key] : null };
  });
  return { steps, offTrack };
}
